<script setup lang="ts">
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

import { ref, onMounted, reactive } from 'vue'

import { Button as KButton } from '@progress/kendo-vue-buttons'

import { useLogger } from 'vue-logger-plugin'

import type { IEventOrderResponse } from '../../types/EventTypes'
import type { IPhoneInput, IPhoneOutput } from '../../types/CommonTypes'
import type { IAoeCustomer } from '../../types/CustomerTypes.js'
import { GetBeeNumberAuthenicatedUser } from '../../stores/SettingsStore.ts'

import { EventGuestCreate } from '../../services/EventService'
import { HostPortalAddressBookCreate } from '../../services/AddressBookService'
import { handleError } from '../../services/ErrorService.ts'
import { SetIsLoading } from '../../services/AoeHelpers'
import { GetCountriesFromLocalStorageOrApi } from '../../services/CountryCultureService'

import PhoneInputControl from '../PhoneInputControl.vue'
import FindEventContactsControl from '../PWS/FindEventContactsControl.vue'

const logger = useLogger()

const emit = defineEmits(["update-cart"]);

const beeNumberAuth = ref<number | null>(0)
const showNewCustomer = ref(false)
const showExistingCustomer = ref(false)

// Form values
const formInitialValues = ref<IAoeCustomer>({
  firstName: '',
  lastName: '',
  emailAddress: '',
} as IAoeCustomer)

// Phone state
const guestPhoneControl = ref<InstanceType<typeof PhoneInputControl> | null>(null)

interface IGuestPhoneCountry {
  text: string
  id: string
}

const guestPhoneCountries = ref<IGuestPhoneCountry[]>([])
const guestPhoneModel = ref<IPhoneOutput | null>(null)

// Manual validation errors
const errors = reactive({
  firstName: '',
  lastName: '',
  emailAddress: '',
})

// Validation functions
const validateFirstName = (): boolean => {
  const nameRegex = /^[\p{L}\s'-]+$/u

  if (!formInitialValues.value.firstName?.trim()) {
    errors.firstName = t('consultantCorner.contactDetails.FirstNameRequired')
    return false
  } else if (!nameRegex.test(formInitialValues.value.firstName)) {
    errors.firstName = t('validators.nameField', { fieldName: t('columns.firstName') })
    return false
  } else if (formInitialValues.value.firstName.trim().length < 2) {
    errors.firstName = t('validators.fieldMinLength', { fieldName: t('columns.firstName'), minimumLength: 2 })
    return false
  } else if (formInitialValues.value.firstName.length > 50) {
    errors.firstName = t('validators.fieldLength', { fieldName: t('columns.firstName'), maximumLength: 50 })
    return false
  }

  errors.firstName = ''
  return true
}

const validateLastName = (): boolean => {
  const nameRegex = /^[\p{L}\s'-]+$/u

  if (!formInitialValues.value.lastName?.trim()) {
    errors.lastName = t('consultantCorner.contactDetails.LastNameRequired')
    return false
  } else if (!nameRegex.test(formInitialValues.value.lastName)) {
    errors.lastName = t('validators.nameField', { fieldName: t('columns.lastName') })
    return false
  } else if (formInitialValues.value.lastName.trim().length < 2) {
    errors.lastName = t('validators.fieldMinLength', { fieldName: t('columns.lastName'), minimumLength: 2 })
    return false
  } else if (formInitialValues.value.lastName.length > 50) {
    errors.lastName = t('validators.fieldLength', { fieldName: t('columns.lastName'), maximumLength: 50 })
    return false
  }

  errors.lastName = ''
  return true
}

const validateEmailAddress = (): boolean => {
  const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/

  if (!formInitialValues.value.emailAddress?.trim()) {
    errors.emailAddress = t('consultantCorner.contactDetails.EmailRequired')
    return false
  } else if (!emailRegex.test(formInitialValues.value.emailAddress)) {
    errors.emailAddress = t('consultantCorner.contactDetails.EmailInvalid')
    return false
  }

  errors.emailAddress = ''
  return true
}

// Phone helper functions
function newGuestPhoneModel(): IPhoneOutput {
  return {
    number: '',
    countryCode: props.eventorder.item.event.defaultCountryCode || '',
    formattedNumber: '',
    codeIdPhoneType: 'HOME',
    extension: null,
  }
}

function loadGuestPhoneCountries() {
  GetCountriesFromLocalStorageOrApi().then(data => {
    if (data?.items) {
      const list: IGuestPhoneCountry[] = []
      for (const country of data.items) {
        list.push({ text: country.countryCode, id: country.countryCode })
      }
      list.sort((a, b) => a.text.localeCompare(b.text))
      guestPhoneCountries.value = list
      if (!guestPhoneModel.value || !guestPhoneModel.value.number) {
        guestPhoneModel.value = newGuestPhoneModel()
      }
    }
  })
}

const props = defineProps<{
  eventorder: IEventOrderResponse
}>()

onMounted(async () => {
  logger.debug('onMounted', 'AoeAddGuestControl')
  beeNumberAuth.value = await GetBeeNumberAuthenicatedUser()
  loadGuestPhoneCountries()
})

function addGuestToEvent(event: Event | null) {
  logger.debug('addGuestToEvent', event)

  let phoneNumber = { number: null, countryCode: null, codeIdPhoneType: null } as IPhoneInput
  const guestPhoneValue = guestPhoneModel.value?.number?.trim() ?? ''

  if (guestPhoneValue.length > 0) {
    phoneNumber = {
      number: guestPhoneValue,
      countryCode: guestPhoneModel.value?.countryCode || props.eventorder.item.event.defaultCountryCode,
      codeIdPhoneType: 'HOME',
    } as IPhoneInput
  }

  HostPortalAddressBookCreate({
    firstName: formInitialValues.value.firstName,
    lastName: formInitialValues.value.lastName,
    emailAddress: formInitialValues.value.emailAddress,
    primaryPhone: phoneNumber,
    addressIdShipping: null,
    eventIdToken: props.eventorder.item.event.eventIdToken,
  }).then((addressBookResp) => {
    logger.debug('AddressBookCreate', addressBookResp)

    if (addressBookResp) {
      const addressBookId = addressBookResp.addressBookId

      SetIsLoading(true)
      EventGuestCreate({
        codeIdEventRole: 'G',
        eventIdToken: props.eventorder.item.event.eventIdToken,
        guestBeeNumber: null,
        addressBookId: addressBookId,
      }).then((eventGuest) => {
        SetIsLoading(false)
        if (eventGuest) {
          updateCartHandler(null)
        }
      })
    }
  }).catch(async (error) => {
    SetIsLoading(false)
    await handleError(error, t)
  })
}

function addNewGuestButtonClickHandler(event: MouseEvent) {
  logger.debug('addNewGuestButtonClickHandler', event)
  showNewCustomer.value = true
  formInitialValues.value = { firstName: '', lastName: '', emailAddress: '' } as IAoeCustomer
  guestPhoneModel.value = newGuestPhoneModel()
  errors.firstName = ''
  errors.lastName = ''
  errors.emailAddress = ''
}

function addExistingGuestButtonClickHandler(event: MouseEvent) {
  logger.debug('addExistingGuestButtonClickHandler', event)
  showExistingCustomer.value = true
}

function onGuestFormSubmitClickHandler(event: Event) {
  event.preventDefault()
  logger.debug('onGuestFormSubmitClickHandler')

  let isValid = true

  // Validate individual fields
  if (!validateFirstName()) isValid = false
  if (!validateLastName()) isValid = false
  if (!validateEmailAddress()) isValid = false

  // Validate phone if number entered
  if (guestPhoneModel.value?.number && guestPhoneControl.value) {
    if (!guestPhoneControl.value.validate()) {
      isValid = false
    }
  }

  if (!isValid) {
    logger.debug('Form validation failed')
    return
  }

  addGuestToEvent(null)
}

function updateCartHandler(event: MouseEvent | null) {
  logger.debug('updateCartHandler AoeTabbedProductEntry', event)
  emit('update-cart', event)
}

</script>

<template>
  <div v-if="props.eventorder != null" class="aoe-add-guest-control">
    <div class="row mt-3 party-order-entry-section-header-row">
      <div class="col-12 party-order-entry-section-header-div add-guest-section-header">
        {{ t('partyOrderEntry.AddGuestSectionHeader') }}
      </div>
    </div>
    <div class="row mt-3 add-guest-label-row">
      <div class="col-12 add-guest-label-div">
        {{ t('partyOrderEntry.Step1AddGuestLabel') }}
      </div>
    </div>
    <div class="row mt-3 add-guest-button-row">
      <div class="col-12 col-md-6 text-center add-new-customer">
        <KButton
          :rounded="'large'"
          class="baseBtn primaryBtn update-cart-button"
          @click="addNewGuestButtonClickHandler($event)"
          >{{ t('partyOrderEntry.AddNewCustomerButton') }}
        </KButton>
      </div>
      <div class="col-12 col-md-6 text-center add-customer-email">
        <KButton
          :rounded="'large'"
          class="baseBtn primaryBtn update-cart-button"
          @click="addExistingGuestButtonClickHandler($event)"
          >{{ t('partyOrderEntry.AddCustomerEmailButton') }}
        </KButton>
      </div>
    </div>
    <div v-if="showNewCustomer" class="row mt-3 add-guest-label-row">
      <div class="col-12 add-guest-label-div">
        {{ t('partyOrderEntry.Step2AddNewGuestLabel') }}
      </div>
    </div>
    <div v-if="showNewCustomer">
      <form @submit.prevent="onGuestFormSubmitClickHandler($event)" id="guestForm">
        <fieldset class="k-form-fieldset">
          <div class="row mb-3">
            <div class="col-12 col-lg-3 form-field">
              <label class="form-label required">{{ t('columns.emailAddress') }}</label>
              <input
                v-model="formInitialValues.emailAddress"
                type="email"
                class="form-input"
                :class="{ 'error': errors.emailAddress }"
                required
                @input="validateEmailAddress"
                @blur="validateEmailAddress"
              />
              <div v-if="errors.emailAddress" class="error-message">{{ errors.emailAddress }}</div>
            </div>

            <div class="col-12 col-lg-3 form-field">
              <label class="form-label required">{{ t('columns.firstName') }}</label>
              <input
                v-model="formInitialValues.firstName"
                type="text"
                class="form-input"
                :class="{ 'error': errors.firstName }"
                required
                @input="validateFirstName"
                @blur="validateFirstName"
              />
              <div v-if="errors.firstName" class="error-message">{{ errors.firstName }}</div>
            </div>

            <div class="col-12 col-lg-3 form-field">
              <label class="form-label required">{{ t('columns.lastName') }}</label>
              <input
                v-model="formInitialValues.lastName"
                type="text"
                class="form-input"
                :class="{ 'error': errors.lastName }"
                required
                @input="validateLastName"
                @blur="validateLastName"
              />
              <div v-if="errors.lastName" class="error-message">{{ errors.lastName }}</div>
            </div>

            <div class="col-12 col-lg-3 form-field">
              <label class="form-label">{{ t('columns.phone') }}</label>
              <PhoneInputControl
                ref="guestPhoneControl"
                class="guestPhoneField"
                v-model="guestPhoneModel"
                :required="false"
                :showExtension="false"
                :showPhoneType="false"
                :countryList="guestPhoneCountries"
              />
            </div>
          </div>

          <div class="row mt-3">
            <div class="col-12 buttonAddGuest">
              <KButton
                ref="btnAddGuest"
                id="btnAddGuest"
                :rounded="'large'"
                :theme-color="'checkoutContinue'"
                type="submit"
                class="btnAddGuest"
              >
                {{ t('eventManagement.AddGuestButton') }}
              </KButton>
              <KButton
                ref="btnAddGuestCancel"
                id="btnAddGuestCancel"
                :rounded="'large'"
                :theme-color="'checkoutContinue'"
                @click="showNewCustomer = false"
                class="baseBtn secondaryBtn add-guest-cancel-button"
              >
                {{ t('eventManagement.AddGuestCancelButton') }}
              </KButton>
            </div>
          </div>
        </fieldset>
      </form>
    </div>
    <div v-if="showExistingCustomer" class="row mt-3 add-guest-label-row">
      <div class="col-12 add-guest-label-div">
        {{ t('partyOrderEntry.Step2AddExistingGuestLabel') }}
      </div>
    </div>
    <div v-if="showExistingCustomer">
      <FindEventContactsControl
        :showAddGuest="true"
        :eventidtoken="props.eventorder.item.event.eventIdToken"
        :beeNumberAuth="beeNumberAuth"
        @guest-grid-refresh="updateCartHandler"
      ></FindEventContactsControl>
    </div>
  </div>
</template>

<style lang="scss">
.add-guest-label-div {
  @include typography-h4;
  @include font-weight-semibold;
}

.add-guest-cancel-button {
  margin-left: 10px;
}

// Form field styling (matches AoeEventManagement)
.form-field {
  display: flex;
  flex-direction: column;
  margin-bottom: $spacing-2;
}

.form-label {
  @include typography-body;
  @include font-weight-semibold;
  margin-bottom: $spacing-1;
}

.form-label.required::after {
  content: ' *';
  color: #dc3545;
}

.form-input {
  padding: $spacing-3;
  border: 1px solid var(--color-border);
  border-radius: $radius-md;
  @include typography-body;
  transition: border-color 0.2s;
}

.form-input:focus {
  outline: none;
  border-color: var(--color-teal);
}

.form-input.error {
  border-color: #dc3545;
}

.form-input.error:focus {
  border-color: #dc3545;
}

.form-field .error-message {
  @include typography-small;
  color: #dc3545;
  margin-top: $spacing-1;
}

.buttonAddGuest {
  text-align: right;
}

// Match PhoneInputControl to Kendo rounded large inputs
.guestPhoneField .phone-input-group {
  width: 100%;
}

.guestPhoneField .country-select,
.guestPhoneField .phone-input {
  @include typography-small;
  padding-top: $spacing-1;
  padding-bottom: $spacing-1;
}

.guestPhoneField .country-select {
  border-radius: $radius-md 0 0 $radius-md;
}

.guestPhoneField .phone-input {
  /* !important required: PhoneInputControl's scoped .phone-input declares border-radius with !important */
  border-radius: 0 $radius-md $radius-md 0 !important;
}

// Ensure phone error message is not italic to match other fields
.guestPhoneField .error-message {
  font-style: normal !important;
}
</style>
